import * as React from "react";
import { Box, Button, Typography, InputAdornment, Skeleton, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CommentIcon from '@mui/icons-material/Comment';
import StyledGrid, { StyledTextSearch, FilterChip } from "../../../../shared/components/StyledGrid";
import { ModalWindow } from "../../../../shared/components/modalWindow/ModalWindow";
import { useGetInvociesMutation, useGetInvoicesStatusesQuery } from "../../../../services/InsuranceService";
import { TableFilter, assessmentClasses, emailStatuses } from "../../invoicesTab/tableFilter/TableFilter";
import useFetchWithMsal from "../../../../utils/useFetchWithMsal";
import { findIdByName } from "../../invoicesTab/InvoicesTab";
import { buildInvoicesBaseColumns } from "../../invoicesTab/invoicesColumns";

export const ApplyPaymentModal = ({ open, onClose, onApply, totalAmount, alreadyApplied }) => {
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });
  const [sortingModel, setSortingModel] = React.useState();
  const [rows, setRows] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [selection, setSelection] = React.useState([]);
  const prevSelectionRef = React.useRef([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  // Persist last-applied filters so pagination/sorting refetches use them
  const [activeFilters, setActiveFilters] = React.useState(null);
  // Note dialog state
  const [noteDialog, setNoteDialog] = React.useState({ open: false, rowId: null, text: "" });

  const { data: invoicesStatuses } = useGetInvoicesStatusesQuery("");
  const filteredStatuses = invoicesStatuses?.filter(s => s.id !== -1) || [];
  const { skip } = useFetchWithMsal();
  const [getInovices, { data, isLoading }] = useGetInvociesMutation("", { skip });

  const handlePaginationModelChange = (newModel) => setPaginationModel(newModel);

  const fetchData = (value, overrideSearch) => {
    // Merge incoming overrides with last-applied filters
    const merged = { ...(activeFilters || {}), ...(value || {}) };
    const activeSearch = overrideSearch !== undefined ? overrideSearch : searchTerm;
    const trimmed = activeSearch?.trim();
    const isNaic = trimmed && /^\d+$/.test(trimmed);
    const statusPayload = (() => {
      // Support All Open via statusIds of open statuses; multi-select path via statusIds
      if (merged?.status === 'All Open') {
        const openIds = (invoicesStatuses || []).filter(s => s?.isOpen).map(s => s.id);
        return { statusIds: openIds };
      }
      if (merged?.statusIds?.length) return { statusIds: merged.statusIds };
      if (merged?.status) {
        const id = findIdByName(invoicesStatuses, merged.status);
        return id ? { statusId: id } : {};
      }
      return {};
    })();

    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: trimmed ? (isNaic ? trimmed : null) : (merged ? merged.naicCode : null),
      memberName: trimmed ? (!isNaic ? trimmed : null) : (merged ? merged.memberName : null),
      ...statusPayload,
      assessmentDate: merged ? merged.assessmentDate : null,
      assessmentId: merged ? merged.assessmentId?.id : null,
      assessmentClass: merged ? findIdByName(assessmentClasses, merged.assessmentClass) : null,
      emailStatus: merged ? findIdByName(emailStatuses, merged.emailStatus) : null,
      invoiceNumber: merged ? merged.invoiceNumber : null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    getInovices(body);
  };

  React.useEffect(() => {
    if (!open) return;
    // Reset modal-local selection and any prior applied amounts
    setSelection([]);
    prevSelectionRef.current = [];
    setRows(prev => prev.map(r => ({ ...r, appliedAmount: '' })));
    fetchData();
  }, [open]);

  React.useEffect(() => {
    if (!isLoading) {
      if (data?.invoices) setRows(data.invoices);
      if (data?.totalCount !== undefined) setTotalCount(data.totalCount);
    }
  }, [isLoading, data]);

  // Refetch when pagination or sorting changes, mirroring PaymentsTab behavior
  React.useEffect(() => {
    if (!open) return;
    const delay = 0;
    const t = setTimeout(() => {
      fetchData(undefined, searchTerm);
    }, delay);
    return () => clearTimeout(t);
  }, [open, paginationModel.page, paginationModel.pageSize, sortingModel?.field, sortingModel?.sort, searchTerm, activeFilters]);

  React.useEffect(() => {
    const h = setTimeout(() => {
      if (paginationModel.page !== 0) setPaginationModel(p => ({ ...p, page: 0 }));
      else fetchData(undefined, searchTerm);
    }, 400);
    return () => clearTimeout(h);
  }, [searchTerm]);

  // Editable Applied Amount column for assigning amounts before applying.
  const appliedAmountCol = {
    field: 'appliedAmount',
    headerName: 'Applied Amount',
    width: 130,
    editable: true,
    renderCell: (params) => {
      const val = Number(params.row?.appliedAmount || 0);
      return val ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
    },
    renderEditCell: (params) => {
      return (
        <input
          type="text"
          value={params.value || ''}
          onChange={(e) => {
            let value = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
            const match = value.match(/^(\d+)(\.?\d{0,2})?/);
            if (match) value = match[0];
            params.api.setEditCellValue({ id: params.id, field: params.field, value });
          }}
          onBlur={(e) => {
            let value = e.target.value;
            if (value.endsWith('.')) value = value.slice(0, -1);
            params.api.setEditCellValue({ id: params.id, field: params.field, value });
          }}
          style={{
            width: '100%',
            padding: '4px',
            textAlign: 'center',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      );
    }
  };

  const columns = React.useMemo(() => {
    const base = buildInvoicesBaseColumns({
      invoicesStatuses: invoicesStatuses || [],
      filteredStatuses: filteredStatuses || [],
      readOnlyStatus: true,
      hideStatusCaret: true,
    });
    // Prepend applied amount and keep a concise subset commonly needed when applying.
    // You can adjust which base columns to include if performance/UI needs tuning.
    return [
      appliedAmountCol,
      ...base.filter(c => ['naicCode', 'memberName', 'assessmentTitle', 'invoiceDate', 'noticeNumber', 'status'].includes(c.field)),
      {
        field: 'activityNote', headerName: 'Note', width: 70,
        renderCell: (params) => {
          const applied = Number(params.row?.appliedAmount || 0) > 0;
          if (!applied) return null; // Only show when payment has been applied to this invoice
          return (
            <IconButton
              size="small"
              title="Add Note"
              onClick={() => setNoteDialog({ open: true, rowId: params.id, text: params.row?.activityNote || params.row?.Note || '' })}
            >
              <CommentIcon fontSize="small" />
            </IconButton>
          );
        }
      }
    ];
  }, [invoicesStatuses, filteredStatuses]);

  const processRowUpdate = (newRow) => {
    const sanitized = { ...newRow };
    if (sanitized.appliedAmount !== undefined && sanitized.appliedAmount !== '') {
      const num = Number(sanitized.appliedAmount);
      if (!Number.isNaN(num)) sanitized.appliedAmount = num;
    }
    setRows(prev => prev.map(r => r.id === sanitized.id ? { ...r, appliedAmount: sanitized.appliedAmount } : r));
    return sanitized;
  };

  const handleSelectionChange = (newSel) => {
    const prevSel = prevSelectionRef.current || [];
    // detect added ids only
    const added = (newSel || []).filter(id => !prevSel.includes(id));
    const removed = (prevSel || []).filter(id => !(newSel || []).includes(id));

    if (added.length) {
      // compute remaining funds: total - alreadyApplied - modal-applied
      const payTotal = Number(totalAmount || 0);
      const appliedBase = Number(alreadyApplied || 0);
      const modalApplied = rows
        .filter(r => (newSel || []).includes(r.id))
        .reduce((sum, r) => sum + Number(r?.appliedAmount || 0), 0);
      let remaining = Math.max(payTotal - appliedBase - modalApplied, 0);

      const getCap = (r) => {
        const candidates = [r?.remainingAmount, r?.balance, r?.outstanding, r?.amountDue, r?.invoiceBalance, r?.amount];
        for (const c of candidates) {
          const n = Number(c);
          if (!Number.isNaN(n) && n > 0) return n;
        }
        return Number.POSITIVE_INFINITY;
      };

      if (remaining > 0) {
        setRows(prev => prev.map(r => {
          if (!added.includes(r.id)) return r;
          const cap = getCap(r);
          const current = Number(r?.appliedAmount || 0);
          let assign = current > 0 ? current : Math.min(remaining, cap);
          assign = Math.min(assign, remaining, cap);
          assign = Math.max(Number.isFinite(assign) ? assign : 0, 0);
          remaining -= assign;
          return { ...r, appliedAmount: assign };
        }));
      }
    }

    if (removed.length) {
      // Clear appliedAmount for deselected rows (set to blank/empty)
      setRows(prev => prev.map(r => removed.includes(r.id) ? { ...r, appliedAmount: '' } : r));
    }

    setSelection(newSel);
    prevSelectionRef.current = newSel;
  };

  // Ensure rows reflect deselection from parent (if a previously linked invoice was unlinked before reopening modal)
  React.useEffect(() => {
    if (!open) return;
    // Any rows carrying an appliedAmount but not selected should remain editable but not pre-populated unless user selects again.
    setRows(prev => prev.map(r => selection.includes(r.id) ? r : r));
  }, [open, selection]);

  return (
    <ModalWindow open={open} onClose={onClose} header="Apply Payment">
      <Box sx={{ width: 900, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <StyledTextSearch
            id="apply-search"
            size="small"
            placeholder="Search NAIC or Member Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.1 }}>
            <Button variant="outlined" onClick={() => fetchData()}>Refresh</Button>
            <TableFilter onApplyFilters={(v) => { setActiveFilters(v || null); fetchData(v); }} hideEmailStatus defaultAllOpen skipDefaultAssessment />
          </Box>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginRight: '15px',
        }}>
          {searchTerm && (
            <FilterChip>
              Search: {searchTerm}
              <IconButton size="small" onClick={() => { setSearchTerm(''); fetchData(undefined, ''); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.naicCode && (
            <FilterChip>
              NAIC Code: {activeFilters.naicCode}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.naicCode; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.memberName && (
            <FilterChip>
              Member Name: {activeFilters.memberName}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.memberName; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.groupName && (
            <FilterChip>
              Group Name: {activeFilters.groupName}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.groupName; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.status && (
            <FilterChip>
              Status: {activeFilters.status}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.status; delete nf.statusIds; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}         
          {activeFilters?.assessmentClass && (
            <FilterChip>
              Assessment Class: {activeFilters.assessmentClass}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.assessmentClass; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.assessmentDate && (
            <FilterChip>
              Assessment Date: {activeFilters.assessmentDate}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.assessmentDate; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.assessmentId?.title && (
            <FilterChip>
              Assessment: {activeFilters.assessmentId.title}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.assessmentId; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
          {activeFilters?.invoiceNumber && (
            <FilterChip>
              Invoice Number: {activeFilters.invoiceNumber}
              <IconButton size="small" onClick={() => { const nf = { ...(activeFilters || {}) }; delete nf.invoiceNumber; setActiveFilters(nf); fetchData(nf); }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </FilterChip>
          )}
        </Box>
        {!rows ? (
          <Box width="900px" height="300px" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rounded" height={34} animation="wave" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={46} animation="wave" />
            ))}
          </Box>
        ) : (
          <StyledGrid
            data={rows}
            width="900px"
            height="300px"
            columns={columns}
            rowCount={totalCount}
            withFilter={false}
            withCheckboxSelection
            onSortingModelChange={(model) => setSortingModel(model[0])}
            pageSizeOptions={[10]}
            paginationModel={paginationModel}
            setPaginationModel={handlePaginationModelChange}
            onRowSelectionModelChange={handleSelectionChange}
            selectionModel={selection}
            hideFooterSelectedRowCount={false}
            processRowUpdate={processRowUpdate}
            isCellEditable={(params) => params.field === 'appliedAmount' && selection.includes(params.row.id)}
            autoFocusEdit={{ field: 'appliedAmount', trigger: 1 }}
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onApply(rows.filter(r => selection.includes(r.id)))}
            disabled={!selection?.length}
          >
            Apply Selected
          </Button>
        </Box>
        <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, rowId: null, text: '' })} maxWidth="sm" fullWidth>
          <DialogTitle>Add Note</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="activity-note"
              type="text"
              fullWidth
              multiline
              minRows={3}
              value={noteDialog.text}
              onChange={(e) => setNoteDialog(prev => ({ ...prev, text: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteDialog({ open: false, rowId: null, text: '' })}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                const { rowId, text } = noteDialog;
                // Update rows
                setRows(prev => prev.map(r => r.id === rowId ? { ...r, activityNote: text, Note: text } : r));
                // If selected, update selection note too
                if (selection?.includes(rowId)) {
                  // selection held locally; consumers pass rows, so updating rows is sufficient for onApply
                }
                setNoteDialog({ open: false, rowId: null, text: '' });
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ModalWindow>
  );
}
